/**
 * Validadores para documentos de identidad españoles (DNI, NIE, CIF, Pasaportes, Otros)
 */

const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
const CIF_LETTERS = 'JABCDEFGHI'; // Para el dígito de control del CIF

export enum IdentityDocumentType {
  DNI = 'DNI',
  NIE = 'NIE',
  CIF = 'CIF',
  SPANISH_PASSPORT = 'SPANISH_PASSPORT',
  FOREIGN_PASSPORT = 'FOREIGN_PASSPORT',
  OTHER = 'OTHER',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

/**
 * Mapea los tipos de documento del frontend al enum del backend (valores numéricos)
 * Backend enums: DNI=0, CIF=2, NIE=4, SpanishPassport=5, ForeignPassport=6, Other=7
 */
export function mapToBackendDocumentType(frontendType: IdentityDocumentType | string): number {
  const typeMap: Record<string, number> = {
    [IdentityDocumentType.DNI]: 0,
    [IdentityDocumentType.NIE]: 4,
    [IdentityDocumentType.CIF]: 2,
    [IdentityDocumentType.SPANISH_PASSPORT]: 5,
    [IdentityDocumentType.FOREIGN_PASSPORT]: 6,
    [IdentityDocumentType.OTHER]: 7,
    [IdentityDocumentType.NOT_AVAILABLE]: 8,
  };

  return typeMap[frontendType] ?? 0; // Por defecto DNI (0)
}

/**
 * Mapea los valores numéricos del backend al enum del frontend
 */
export function mapFromBackendDocumentType(backendValue: number | null | undefined): IdentityDocumentType {
  if (backendValue === null || backendValue === undefined) {
    return IdentityDocumentType.DNI;
  }

  const typeMap: Record<number, IdentityDocumentType> = {
    0: IdentityDocumentType.DNI,
    3: IdentityDocumentType.DNI,      // NIF legacy → DNI
    2: IdentityDocumentType.CIF,
    4: IdentityDocumentType.NIE,
    5: IdentityDocumentType.SPANISH_PASSPORT,
    6: IdentityDocumentType.FOREIGN_PASSPORT,
    7: IdentityDocumentType.OTHER,
    8: IdentityDocumentType.NOT_AVAILABLE,
  };

  return typeMap[backendValue] ?? IdentityDocumentType.DNI;
}

/**
 * Valida un DNI español (8 dígitos + letra de control)
 */
export function validarDNI(raw: string): { valido: boolean; error?: string } {
  const value = raw.trim().toUpperCase().replace(/[\s\-\.]/g, '');
  const match = value.match(/^(\d{8})([A-Z])$/);

  if (!match) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  const [, digits, letter] = match;
  const expected = DNI_LETTERS[parseInt(digits, 10) % 23];

  if (letter !== expected) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  return { valido: true };
}

/**
 * Valida un NIE español (X/Y/Z + 7 dígitos + letra de control)
 */
export function validarNIE(raw: string): { valido: boolean; error?: string } {
  const value = raw.trim().toUpperCase().replace(/[\s\-.]/g, '');
  const match = value.match(/^([XYZ])(\d{7})([A-Z])$/);

  if (!match) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  const [, prefix, digits, letter] = match;
  const prefixMap: Record<string, string> = { X: '0', Y: '1', Z: '2' };
  const numeric = parseInt(prefixMap[prefix] + digits, 10);
  const expected = DNI_LETTERS[numeric % 23];

  if (letter !== expected) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  return { valido: true };
}

/**
 * Valida un CIF español (1 letra + 7 dígitos + 1 dígito/letra de control)
 * Formato: [A-J][0-9]{7}[0-9A-J]
 */
export function validarCIF(raw: string): { valido: boolean; error?: string } {
  const value = raw.trim().toUpperCase().replace(/[\s\-.]/g, '');
  const match = value.match(/^([ABCDEFGHJNPQRSUVW])(\d{7})([0-9A-J])$/);

  if (!match) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  const [, letter, digits, control] = match;

  // Calcular el dígito de control
  const sum = digits.split('').reduce((acc, digit, index) => {
    const num = parseInt(digit);
    const doubledNum = index % 2 === 0 ? num * 2 : num;
    const sumDigits = doubledNum > 9 ? Math.floor(doubledNum / 10) + (doubledNum % 10) : doubledNum;
    return acc + sumDigits;
  }, 0);

  const unitDigit = (10 - (sum % 10)) % 10;
  const expectedDigit = unitDigit.toString();
  const expectedLetter = CIF_LETTERS[unitDigit];

  // El control puede ser un dígito o una letra según el tipo de empresa
  const isValid = control === expectedDigit || control === expectedLetter;

  if (!isValid) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  return { valido: true };
}

/**
 * Valida un pasaporte español
 * Formato: 3 letras + 6 dígitos (9 caracteres en total)
 * Ej: ABC123456, PAA123456
 */
export function validarPasaporteEspanol(raw: string): { valido: boolean; error?: string } {
  const value = raw.trim().toUpperCase().replace(/[\s\-]/g, '');

  if (!/^[A-Z]{3}[0-9]{6}$/.test(value)) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  return { valido: true };
}

/**
 * Valida un pasaporte (español o extranjero)
 * Validación: entre 6 y 9 caracteres alfanuméricos
 */
export function validarPasaporte(raw: string): { valido: boolean; error?: string } {
  const value = raw.trim().replace(/[\s\-]/g, '');

  if (!/^[A-Za-z0-9]{6,9}$/.test(value)) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  return { valido: true };
}

/**
 * Valida un NIF (igual que DNI, por compatibilidad con el backend)
 */
export function validarNIF(raw: string): { valido: boolean; error?: string } {
  return validarDNI(raw);
}

/**
 * Valida un CID (Carnet de Identitat de Resident o similar)
 * Formato flexible pero con reglas básicas:
 * - Mínimo 5 caracteres
 * - Máximo 20 caracteres
 * - Solo alfanuméricos (sin espacios ni caracteres especiales, salvo guión)
 * - Puede empezar con letras o números
 */
export function validarCID(raw: string): { valido: boolean; error?: string } {
  const value = raw.trim().toUpperCase().replace(/[\s]/g, ''); // Eliminar espacios pero mantener guiones

  // Validar longitud
  if (!value || value.length < 5) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  if (value.length > 20) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  // Validar que solo contenga alfanuméricos y guiones
  if (!/^[A-Z0-9\-]+$/.test(value)) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  // Validar que no sea solo números o solo letras (un poco más restrictivo)
  const hasLetters = /[A-Z]/.test(value);
  const hasNumbers = /[0-9]/.test(value);

  if (!hasLetters && !hasNumbers) {
    return { valido: false, error: 'Número de identificación no válido' };
  }

  return { valido: true };
}

/**
 * Valida un documento de identidad basado en su tipo
 * Tipos soportados: DNI, NIE, CIF, Pasaporte español, Pasaporte extranjero, Otros
 */
export function validarDocumentoIdentidad(
  value: string,
  type: IdentityDocumentType | string
): { valido: boolean; error?: string } {
  switch (type) {
    case IdentityDocumentType.DNI:
      return validarDNI(value);
    case IdentityDocumentType.NIE:
      return validarNIE(value);
    case IdentityDocumentType.CIF:
      return validarCIF(value);
    case IdentityDocumentType.SPANISH_PASSPORT:
      return validarPasaporteEspanol(value);
    case IdentityDocumentType.FOREIGN_PASSPORT:
      return validarPasaporte(value);
    case IdentityDocumentType.OTHER:
    case IdentityDocumentType.NOT_AVAILABLE:
      return { valido: true };
    default:
      return { valido: false, error: 'Número de identificación no válido' };
  }
}
